import { Accordion } from "@/components/ui/accordion";
import { SupportedLocale } from "@/i18n";
import { Artwork } from "@/lib/types";
import {
  ComputedFields,
  MergedDisplayData,
  RelatedFieldsWithAttachments,
} from "../types";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { CreditsAccordion } from "./CreditsAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { HistoryAccordion } from "./HistoryAccordion";
import { RelatedWorksAccordion } from "./RelatedWorksAccordion";
import { TirageKnownDescriptionAccordion } from "./TirageKnownDescriptionAccordion";
import { TiragesAccordion } from "./TiragesAccordion";

interface ArtworkAccordionsProps {
  displayData: MergedDisplayData;
  data: Artwork;
  computedFields: ComputedFields;
  relatedFieldsWithAttachments: RelatedFieldsWithAttachments;
  relationNotEmpty: boolean;
  isTirage: boolean;
  isPhotoMaster: boolean;
  isPhotoModuleEnabled: boolean;
  locale: SupportedLocale;
  t: (key: string, options?: any) => string;
}

export const AccordionClass =
  "[&>span>i]:text-[14px] text-sm leading-[110%] subpixel-antialiased font-sans";

export function ArtworkAccordions({
  displayData,
  data,
  computedFields,
  relatedFieldsWithAttachments,
  relationNotEmpty,
  isTirage,
  isPhotoMaster,
  isPhotoModuleEnabled,
  locale,
  t,
}: ArtworkAccordionsProps) {
  const hasHistory =
    (displayData.artworkHistory?.length ?? 0) > 0 ||
    (displayData.tirageHistory?.length ?? 0) > 0;

  // When viewing a Tirage, ensure we check for tirage-specific relations
  // The mergeDisplayData function normalizes tirage relations to artwork format,
  // but we also check raw tirage data as a fallback to ensure completeness
  const tirageData = isTirage ? (data as any)?.tirageData : null;

  // Check for exhibitions: displayData should have normalized tirage relations,
  // but we also check raw tirage data when viewing a Tirage
  const hasExhibitions =
    (displayData.artworkInExhibitions?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInExhibition?.length ?? 0) > 0);

  // Check for bibliography
  const hasBibliography =
    (displayData.artworkInBibliography?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInBibliography?.length ?? 0) > 0);

  // Check for auctions
  const hasAuctions =
    (displayData.artworkInAuctions?.length ?? 0) > 0 ||
    (isTirage && (tirageData?.tirageInAuction?.length ?? 0) > 0);

  return (
    <Accordion
      type="single"
      collapsible
      className="font-medium text-base text-gray-800 mb-32"
    >
      {hasHistory && (
        <HistoryAccordion
          historyCalculated={computedFields.historyCalculated}
          tirageHistoryCalculated={computedFields.tirageHistoryCalculated}
          t={t}
        />
      )}
      {hasExhibitions && computedFields.exhibitionsCalculated && (
        <ExhibitionsAccordion
          exhibitionsCalculated={computedFields.exhibitionsCalculated}
          t={t}
        />
      )}
      {hasBibliography && computedFields.bibliographyCalculated && (
        <BibliographyAccordion
          bibliographyCalculated={computedFields.bibliographyCalculated}
          t={t}
        />
      )}
      {hasAuctions && computedFields.auctionCalculated && (
        <AuctionsAccordion
          auctionCalculated={computedFields.auctionCalculated}
          t={t}
        />
      )}
      <CreditsAccordion
        displayData={displayData}
        isPhotoMaster={isPhotoMaster}
        isTirage={isTirage}
        t={t}
      />
      <TiragesAccordion
        displayData={displayData}
        isTirage={isTirage}
        isPhotoModuleEnabled={isPhotoModuleEnabled}
        locale={locale}
        t={t}
      />
      <TirageKnownDescriptionAccordion
        data={data}
        isPhotoMaster={isPhotoMaster}
        locale={locale}
        t={t}
      />
      <RelatedWorksAccordion
        relatedFieldsWithAttachments={relatedFieldsWithAttachments}
        relationNotEmpty={relationNotEmpty}
        t={t}
      />
    </Accordion>
  );
}
